import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { Search, Edit2, Trash2, ChevronDown, X, ArrowUp, ArrowDown, Check } from 'lucide-react';
import { format } from 'date-fns';
import { HttpRequestContext } from '../../../Blocks/HttpRequestProvider';
import TableHeader from '../../../Components/TableHeader';
import TablePagination from '../../../Components/TablePagination';

/**
 * Reusable client table component that can be used in different contexts:
 * 1. Full table in client management page
 * 2. Selection table in invoice page for choosing clients
 * 
 * This component now handles client fetching and management internally to reduce code duplication
 */
const ClientTable = ({
  // Optional array of clients if provided by parent component
  clients: externalClients = null,
  // Optional loading state if provided by parent component
  isLoading: externalLoading = false,
  // Callback for when client selection changes
  onSelectClient = null, 
  // Callback when a client edit is requested
  onEdit = null, 
  // Callback when a client delete is requested
  onDelete = null, 
  // Currently selected client ID
  selectedClientId = null, 
  // Whether this is for selecting a client (e.g. in invoice)
  selectionMode = false, 
  // Initial column configuration
  initialColumns = null, 
  // Optional callback to refresh the client list
  onClientListRefresh = null,
  // Whether to handle client fetching internally (or use external clients)
  handleClientFetching = externalClients ? false : true,
  // Whether to show add client modal functionality
  showAddClientModal = selectionMode,
  // Callback when a client is created
  onClientCreated = null,
  // Title to display above the client table
  title = null,
  // Whether to show the header action buttons (columns, filters, export, new client)
  showHeaderButtons = selectionMode,
  // Callback when a client edit is requested
  onClientEdit = null,
  // Callback when a client delete is requested
  onClientDelete = null,
  // Callback when column visibility changes
  onColumnVisibilityChange = null,
}) => {
  const { sendRequest } = useContext(HttpRequestContext);
  
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterActive, setFilterActive] = useState(null); // null = all, true = active, false = inactive
  
  // Column settings state
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const columnSettingsRef = useRef(null);
  const columnSettingsBtnRef = useRef(null);
  
  // Modal refs
  const addClientModalRef = useRef(null);
  const editClientModalRef = useRef(null);
  
  // Get columns from props or use defaults
  const [availableColumns, setAvailableColumns] = useState(initialColumns || [
    { id: 'clientInfo', name: 'Client / Société', visible: true, sortable: true },
    { id: 'contact', name: 'Contact', visible: true, sortable: true },
    { id: 'tva', name: 'TVA/BCE', visible: true, sortable: true },
    { id: 'status', name: 'Statut', visible: true, sortable: true },
    { id: 'invoiceCount', name: 'Factures', visible: selectionMode ? false : true, sortable: true },
    { id: 'invoiceNumber', name: 'Dernière Facture', visible: selectionMode ? false : true, sortable: true },
    { id: 'invoiceStatus', name: 'Statut de Facture', visible: selectionMode ? false : true, sortable: true },
    { id: 'totalBilled', name: 'Total Facturé', visible: selectionMode ? false : true, sortable: true },
    { id: 'createdAt', name: 'Date de Création', visible: false, sortable: true },
    { id: 'actions', name: 'Actions', visible: true, sortable: false, align: 'right' }
  ]);
  
  // Storage key for localStorage - different for selectionMode vs regular mode
  const storageKey = useMemo(() => 
    selectionMode ? 'clientSelectionTableColumns' : 'clientManagementTableColumns', 
  [selectionMode]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(selectionMode ? 10 : 5);
  const [pageSizeOptions] = useState([5, 10, 25, 50]);
  
  // Internal client management state
  const [internalClients, setInternalClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newClient, setNewClient] = useState({
    firstname: '',
    lastname: '',
    name: '',
    address: '',
    city: '',
    vatNumber: '',
    bceNumber: '',
    email: '',
    phone: '',
    status: 'active'
  });
  
  // Determine which clients to use (internal or external)
  const clients = externalClients || internalClients;
  const isLoading = externalLoading || isLoadingClients;
  
  // Debug logging
  useEffect(() => {
    console.log('ClientTable received external clients:', externalClients);
    console.log('ClientTable using clients:', clients);
  }, [externalClients, clients]);
  
  // Fetch clients from API (if handling internally)
  const fetchClients = useCallback(async () => {
    if (!handleClientFetching) return;
    
    try {
      setIsLoadingClients(true);
      const response = await sendRequest({
        apiPath: 'invoicing/clients/with-invoices',
        type: 'GET'
      });
      
      if (response && response.clients) {
        setInternalClients(response.clients);
      } else {
        console.error('Failed to fetch client data:', response);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setIsLoadingClients(false);
    }
  }, [handleClientFetching, sendRequest]);

  // Load clients on mount if handling internally
  useEffect(() => {
    if (handleClientFetching) {
      fetchClients();
    }
  }, [handleClientFetching, fetchClients]);
  
  // Handle add client
  const handleAddClient = async () => {
    try {
      // Basic validation
      if (!newClient.name && !newClient.firstname && !newClient.lastname) {
        alert('Le nom du client ou nom de société est obligatoire');
        return;
      }
      
      const formData = {
        firstname: newClient.firstname || '',
        lastname: newClient.lastname || '',
        entity_name: newClient.name,
        address_line1: newClient.address,
        address_line2: newClient.city,
        vat_number: newClient.vatNumber,
        bce_number: newClient.bceNumber || '',
        email: newClient.email,
        phone: newClient.phone,
        status: newClient.status
      };
      
      const response = await sendRequest({
        apiPath: 'invoicing/clients/create',
        type: 'POST',
        request: formData
      });
      
      if (response && (response.success || response.client || response.id)) {
        // Reset form
        setIsAddClientModalOpen(false);
        setNewClient({
          firstname: '',
          lastname: '',
          name: '',
          address: '',
          city: '',
          vatNumber: '',
          bceNumber: '',
          email: '',
          phone: '',
          status: 'active'
        });
        
        // Refresh client list
        await fetchClients();
        
        // Handle the newly created client
        const createdClient = response.client || {
          id: response.id,
          ...formData
        };
        
        // If callback provided, call it with the new client
        if (onClientCreated) {
          onClientCreated(createdClient);
        }
      } else {
        console.error('Failed to save client:', response);
        alert('Une erreur est survenue lors de la sauvegarde du client.');
      }
    } catch (err) {
      console.error('Error saving client:', err);
      alert('Une erreur est survenue lors de la sauvegarde du client.');
    }
  };
  
  // Helper for formatting dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  // Format amount for display
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '0,00 €';
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR'
    }).format(amount);
  };
  
  // Get client display name
  const getClientName = (client) => {
    // If there is a client name (first and/or last name)
    const hasClientName = (client.firstname || client.lastname);
    
    if (hasClientName && client.entity_name) {
      // Case 1: Both client name and company name exist
      return `${client.firstname || ''} ${client.lastname || ''}`.trim();
    } else if (client.entity_name) {
      // Case 2: Only company name exists
      return client.entity_name;
    } else {
      // Default: Just return available names
      return `${client.firstname || ''} ${client.lastname || ''}`.trim() || 'Unnamed Client';
    }
  };
  
  // Get secondary line for client info
  const getClientSecondaryInfo = (client) => {
    const hasClientName = (client.firstname || client.lastname);
    
    if (hasClientName && client.entity_name) {
      // Case 1: If both client name and company exist, show company as secondary
      return client.entity_name;
    } else if (client.entity_name && client.address) {
      // Case 2: If company and address exist (but no client name), show address as secondary
      return client.address + (client.address_line2 ? `, ${client.address_line2}` : '');
    } else if (client.address) {
      // Default: Just show address if available
      return client.address + (client.address_line2 ? `, ${client.address_line2}` : '');
    }
    return '';
  };
  
  // Filtering logic
  const filteredClients = useMemo(() => {
    return clients.filter(clientEntry => {
      const client = clientEntry.client || clientEntry;
      
      // Search filter
      const matchesSearch = searchTerm === '' || 
        (client.firstname && client.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.lastname && client.lastname.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.entity_name && client.entity_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.vat_number && client.vat_number.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Active status filter
      const matchesActiveFilter = filterActive === null || 
        (filterActive === true && client.status === 'active') || 
        (filterActive === false && client.status !== 'active');
      
      return matchesSearch && matchesActiveFilter;
    });
  }, [clients, searchTerm, filterActive]);
  
  // Sorting logic
  const sortedClients = useMemo(() => {
    let sortableItems = [...filteredClients];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        // Handle nested properties or complex values
        let aValue, bValue;
        const clientA = a.client || a;
        const clientB = b.client || b;
        
        switch(sortConfig.key) {
          case 'clientInfo':
            aValue = clientA.entity_name ? clientA.entity_name.toLowerCase() : `${clientA.firstname || ''} ${clientA.lastname || ''}`.toLowerCase();
            bValue = clientB.entity_name ? clientB.entity_name.toLowerCase() : `${clientB.firstname || ''} ${clientB.lastname || ''}`.toLowerCase();
            break;
          case 'contact':
            aValue = (clientA.email || '').toLowerCase();
            bValue = (clientB.email || '').toLowerCase();
            break;
          case 'tva':
            aValue = (clientA.vat_number || '').toLowerCase();
            bValue = (clientB.vat_number || '').toLowerCase();
            break;
          case 'invoiceNumber':
            aValue = a.latest_invoice ? a.latest_invoice.num_document : '';
            bValue = b.latest_invoice ? b.latest_invoice.num_document : '';
            break;
          case 'invoiceCount':
            aValue = a.invoice_count || 0;
            bValue = b.invoice_count || 0;
            break;
          case 'invoiceStatus':
            aValue = a.latest_invoice ? a.latest_invoice.status : '';
            bValue = b.latest_invoice ? b.latest_invoice.status : '';
            break;
          case 'totalBilled':
            aValue = a.total_facture || 0;
            bValue = b.total_facture || 0;
            break;
          case 'createdAt':
            aValue = clientA.creation_date ? new Date(clientA.creation_date) : new Date(0);
            bValue = clientB.creation_date ? new Date(clientB.creation_date) : new Date(0);
            break;
          case 'status':
            aValue = clientA.status || '';
            bValue = clientB.status || '';
            break;
          default:
            aValue = clientA[sortConfig.key] || '';
            bValue = clientB[sortConfig.key] || '';
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredClients, sortConfig]);
  
  // Request sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Get sort direction for a column
  const getSortDirection = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction;
    }
    return null;
  };
  
  // Pagination calculations
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedClients.slice(indexOfFirstItem, indexOfLastItem);
  
  // Pagination handlers
  const goToPage = (pageNumber) => {
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
  };
  
  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };
  
  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };
  
  // Update pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  
  // Ensure current page is valid when changing items per page
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, itemsPerPage]);
  
  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    if (onSelectClient) {
      onSelectClient(client);
    }
  };
  
  // Open add client modal
  const openAddClientModal = () => {
    setIsAddClientModalOpen(true);
  };
  
  // Open edit client modal
  const handleEditClient = (client) => {
    // If external edit handler is provided, use it
    if (onClientEdit) {
      onClientEdit(client);
    } else if (onEdit) {
      onEdit(client);
    } else {
      // Otherwise, use internal edit functionality
      setEditingClient({
        id: client.id,
        firstname: client.firstname || '',
        lastname: client.lastname || '',
        name: client.entity_name || '',
        address: client.address_line1 || client.address || '',
        city: client.address_line2 || client.city || '',
        vatNumber: client.vat_number || '',
        bceNumber: client.bce_number || '',
        email: client.email || '',
        phone: client.phone || '',
        status: client.status || 'active'
      });
      setIsEditModalOpen(true);
    }
  };
  
  // Handle delete client
  const handleDeleteClient = (clientId) => {
    // If external delete handler is provided, use it
    if (onClientDelete) {
      onClientDelete(clientId);
    } else if (onDelete) {
      onDelete(clientId);
    } else {
      // Otherwise, use internal delete functionality
      const clientToRemove = clients.find(item => 
        (item.client && item.client.id === clientId) || item.id === clientId
      );
      setClientToDelete(clientToRemove?.client || clientToRemove);
      setShowDeleteModal(true);
    }
  };
  
  // Confirm client deletion
  const confirmDelete = async () => {
    if (clientToDelete) {
      try {
        const response = await sendRequest({
          apiPath: `invoicing/clients/${clientToDelete.id}`,
          type: 'DELETE'
        });
        
        if (response && response.success) {
          // Remove client from internal list
          setInternalClients(prevClients => 
            prevClients.filter(item => 
              (item.client && item.client.id !== clientToDelete.id) || 
              (item.id !== clientToDelete.id)
            )
          );
          
          // Close modal
          setShowDeleteModal(false);
          setClientToDelete(null);
          
          // Refresh client list if necessary
          if (handleClientFetching) {
            fetchClients(); // Refetch when we're handling clients internally
          }
          
          // Refresh client list if callback is provided
          if (onClientListRefresh) {
            onClientListRefresh();
          }
        } else {
          console.error('Failed to delete client:', response);
          alert('Une erreur est survenue lors de la suppression du client.');
        }
      } catch (err) {
        console.error('Error deleting client:', err);
        alert('Une erreur est survenue lors de la suppression du client.');
      }
    }
  };
  
  // Handle save edited client
  const handleSaveEditedClient = async () => {
    try {
      // Basic validation
      if (!editingClient.name && !editingClient.firstname && !editingClient.lastname) {
        alert('Le nom du client ou nom de société est obligatoire');
        return;
      }
      
      const formData = {
        id: editingClient.id,
        firstname: editingClient.firstname || '',
        lastname: editingClient.lastname || '',
        entity_name: editingClient.name,
        address_line1: editingClient.address,
        address_line2: editingClient.city,
        vat_number: editingClient.vatNumber,
        bce_number: editingClient.bceNumber || '',
        email: editingClient.email,
        phone: editingClient.phone,
        status: editingClient.status
      };
      
      const response = await sendRequest({
        apiPath: `invoicing/clients/${editingClient.id}`,
        type: 'POST',
        request: formData
      });
      
      if (response && (response.success || response.client || response.id)) {
        // Reset form
        setIsEditModalOpen(false);
        setEditingClient(null);
        
        // Refresh client list
        if (handleClientFetching) {
          await fetchClients();
        }
        
        // Refresh client list if callback is provided
        if (onClientListRefresh) {
          onClientListRefresh();
        }
      } else {
        console.error('Failed to update client:', response);
        alert('Une erreur est survenue lors de la mise à jour du client.');
      }
    } catch (err) {
      console.error('Error updating client:', err);
      alert('Une erreur est survenue lors de la mise à jour du client.');
    }
  };
  
  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    const updatedColumns = availableColumns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setAvailableColumns(updatedColumns);
    
    // Storage is handled by useEffect now
    // localStorage.setItem('clientTableColumns', JSON.stringify(updatedColumns));
    
    // Notify parent component if callback provided
    if (typeof onColumnVisibilityChange === 'function') {
      onColumnVisibilityChange(updatedColumns);
    }
  };
  
  // Initialize column state management
  useEffect(() => {
    // Priority order:
    // 1. Use provided initialColumns if they exist (parent state)
    // 2. Use localStorage saved settings if available (local persistence)
    // 3. Fall back to default columns (first-time initialization)
    
    if (initialColumns && initialColumns.length > 0) {
      setAvailableColumns(initialColumns);
    } else {
      const savedColumns = localStorage.getItem(storageKey);
      if (savedColumns) {
        try {
          const parsedColumns = JSON.parse(savedColumns);
          setAvailableColumns(parsedColumns);
        } catch (error) {
          console.error('Error loading saved columns:', error);
        }
      }
    }
  }, [initialColumns, storageKey]);
  
  // Save columns to localStorage when they change
  useEffect(() => {
    if (availableColumns && availableColumns.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(availableColumns));
    }
  }, [availableColumns, storageKey]);
  
  // Close column settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnSettings &&
        columnSettingsRef.current && 
        !columnSettingsRef.current.contains(event.target) &&
        columnSettingsBtnRef.current && 
        !columnSettingsBtnRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);
  
  // Render page numbers for pagination
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    
    if (startPage > 1) {
      pageNumbers.push(
        <button 
          key={1} 
          onClick={() => goToPage(1)}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          1
        </button>
      );
      
      if (startPage > 2) {
        pageNumbers.push(
          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
            i === currentPage ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(
          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }
      
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => goToPage(totalPages)}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }
    
    return pageNumbers;
  };
  
  return (
    <div className="client-table-container">
      {/* Header with title and action buttons */}
      <div className="flex justify-between items-center mb-4">
        {title && <h2 className="text-xl font-semibold">{title}</h2>}
        {showHeaderButtons && (
          <div className="flex gap-2">
            <div className="relative">
              <button 
                ref={columnSettingsBtnRef}
                className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
                onClick={() => setShowColumnSettings(!showColumnSettings)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Colonnes</span>
              </button>
              
              {/* Column Settings Dropdown */}
              {showColumnSettings && (
                <div 
                  ref={columnSettingsRef}
                  className="absolute w-64 bg-white rounded-lg shadow-lg z-10 border border-gray-200"
                  style={{ 
                    top: "100%",
                    right: "0",
                    marginTop: "5px"
                  }}
                >
                  <div className="p-3 border-b border-gray-200">
                    <h3 className="font-medium text-gray-700">Colonnes affichées</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {availableColumns.map(column => (
                      <div 
                        key={column.id} 
                        className="flex items-center justify-between p-3 hover:bg-gray-50"
                      >
                        <span className="text-sm text-gray-700">{column.name}</span>
                        <button 
                          onClick={() => column.id !== 'actions' && toggleColumnVisibility(column.id)}
                          className={`w-5 h-5 rounded flex items-center justify-center ${
                            column.visible ? 'bg-blue-500 text-white' : 'border border-gray-300'
                          } ${column.id === 'actions' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                          disabled={column.id === 'actions'}
                        >
                          {column.visible && <Check size={14} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button 
              className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-md px-4 py-2 text-gray-400 cursor-not-allowed"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Filtres</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-md px-4 py-2 text-gray-400 cursor-not-allowed"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Exporter</span>
            </button>
            <button 
              className="flex items-center gap-2 bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700"
              onClick={openAddClientModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Nouveau Client</span>
            </button>
          </div>
        )}
      </div>

      {/* Search bar and filters */}
      <div className="mb-4 flex items-center justify-between">
        <div className="relative w-1/2">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md ${filterActive === null ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setFilterActive(null)}
          >
            Tous
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md ${filterActive === true ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setFilterActive(true)}
          >
            Actifs
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md ${filterActive === false ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setFilterActive(false)}
          >
            Inactifs
          </button>
        </div>
      </div>

      {/* Clients table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <TableHeader
            columns={availableColumns.filter(col => col.visible)}
            sortConfig={sortConfig}
            onSort={requestSort}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={availableColumns.filter(col => col.visible).length} className="px-6 py-4 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span className="ml-2">Chargement...</span>
                  </div>
                </td>
              </tr>
            ) : currentItems.length > 0 ? (
              currentItems.map((clientEntry, index) => {
                const client = clientEntry.client || clientEntry;
                const isSelected = selectedClientId === client.id;
                
                return (
                  <tr 
                    key={client.id} 
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} 
                               ${selectionMode ? 'cursor-pointer hover:bg-blue-100 transition duration-150' : 'hover:bg-gray-100 transition duration-150'} 
                               ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                    onClick={() => selectionMode && handleSelectClient(client)}
                  >
                    {availableColumns.find(col => col.id === 'clientInfo')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{getClientName(client)}</div>
                        <div className="text-sm text-gray-500">
                          {getClientSecondaryInfo(client)}
                        </div>
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'contact')?.visible && (
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{client.email || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{client.phone || ''}</div>
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'tva')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {client.vat_number || 'N/A'}
                          {client.bce_number && (
                            <div className="text-xs text-gray-400">BCE: {client.bce_number}</div>
                          )}
                        </div>
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'status')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {client.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'invoiceCount')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {clientEntry.invoice_count || 0}
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'totalBilled')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatAmount(clientEntry.total_facture)}
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'invoiceNumber')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {clientEntry.latest_invoice ? (
                          <div>
                            <div>{clientEntry.latest_invoice.num_document}</div>
                            <div className="text-xs text-gray-400">{formatDate(clientEntry.latest_invoice.date_emission)}</div>
                          </div>
                        ) : 'Aucune facture'}
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'invoiceStatus')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {clientEntry.latest_invoice ? (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            clientEntry.latest_invoice.status === 'Brouillon' ? 'bg-gray-100 text-gray-800' : 
                            clientEntry.latest_invoice.status === 'Envoyée' ? 'bg-blue-100 text-blue-800' : 
                            clientEntry.latest_invoice.status === 'Premier rappel' ? 'bg-yellow-100 text-yellow-800' : 
                            clientEntry.latest_invoice.status === 'Deuxième Rappel' ? 'bg-orange-100 text-orange-800' : 
                            clientEntry.latest_invoice.status === 'Troisième Rappel' ? 'bg-red-100 text-red-800' : 
                            clientEntry.latest_invoice.status === 'Payée' ? 'bg-green-100 text-green-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {clientEntry.latest_invoice.status}
                          </span>
                        ) : 'N/A'}
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'createdAt')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(client.creation_date)}
                      </td>
                    )}
                    
                    {availableColumns.find(col => col.id === 'actions')?.visible && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClient(client);
                            }}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClient(client.id);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={availableColumns.filter(col => col.visible).length} className="px-6 py-4 text-center text-gray-500">
                  Aucun client ne correspond à votre recherche
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {!selectionMode || currentItems.length > 0 ? (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={sortedClients.length}
          pageSizeOptions={pageSizeOptions}
          onPageChange={goToPage}
          onPageSizeChange={setItemsPerPage}
        />
      ) : null}
      
      {/* Add Client Modal */}
      {isAddClientModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] modal-overlay"
          onClick={(e) => {
            // Close the modal if clicking outside the modal content
            if (addClientModalRef.current && !addClientModalRef.current.contains(e.target)) {
              setIsAddClientModalOpen(false);
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()} ref={addClientModalRef}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Ajouter un nouveau client
            </h3>
            
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input
                    type="text"
                    id="firstname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.firstname || ''}
                    onChange={(e) => setNewClient({...newClient, firstname: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="lastname" className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    id="lastname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.lastname || ''}
                    onChange={(e) => setNewClient({...newClient, lastname: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="entity_name" className="block text-sm font-medium text-gray-700">Nom de société</label>
                  <input
                    type="text"
                    id="entity_name"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.name}
                    onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    id="phone"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
                  <select
                    id="status"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.status}
                    onChange={(e) => setNewClient({...newClient, status: e.target.value})}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Adresse (Ligne 1)</label>
                  <input
                    type="text"
                    id="address"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.address}
                    onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700">Adresse (Ligne 2)</label>
                  <input
                    type="text"
                    id="address_line2"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.city}
                    onChange={(e) => setNewClient({...newClient, city: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700">Numéro TVA</label>
                  <input
                    type="text"
                    id="vat_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.vatNumber}
                    onChange={(e) => setNewClient({...newClient, vatNumber: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="bce_number" className="block text-sm font-medium text-gray-700">Numéro BCE</label>
                  <input
                    type="text"
                    id="bce_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newClient.bceNumber || ''}
                    onChange={(e) => setNewClient({...newClient, bceNumber: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setIsAddClientModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={handleAddClient}
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Client Modal */}
      {isEditModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] modal-overlay"
          onClick={(e) => {
            // Close the modal if clicking outside the modal content
            if (editClientModalRef.current && !editClientModalRef.current.contains(e.target)) {
              setIsEditModalOpen(false);
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()} ref={editClientModalRef}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Modifier le client
            </h3>
            
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input
                    type="text"
                    id="firstname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.firstname || ''}
                    onChange={(e) => setEditingClient({...editingClient, firstname: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="lastname" className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    id="lastname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.lastname || ''}
                    onChange={(e) => setEditingClient({...editingClient, lastname: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="entity_name" className="block text-sm font-medium text-gray-700">Nom de société</label>
                  <input
                    type="text"
                    id="entity_name"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.name || ''}
                    onChange={(e) => setEditingClient({...editingClient, name: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.email || ''}
                    onChange={(e) => setEditingClient({...editingClient, email: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    id="phone"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.phone || ''}
                    onChange={(e) => setEditingClient({...editingClient, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
                  <select
                    id="status"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.status || 'active'}
                    onChange={(e) => setEditingClient({...editingClient, status: e.target.value})}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Adresse (Ligne 1)</label>
                  <input
                    type="text"
                    id="address"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.address || ''}
                    onChange={(e) => setEditingClient({...editingClient, address: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700">Adresse (Ligne 2)</label>
                  <input
                    type="text"
                    id="address_line2"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.city || ''}
                    onChange={(e) => setEditingClient({...editingClient, city: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700">Numéro TVA</label>
                  <input
                    type="text"
                    id="vat_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.vatNumber || ''}
                    onChange={(e) => setEditingClient({...editingClient, vatNumber: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="bce_number" className="block text-sm font-medium text-gray-700">Numéro BCE</label>
                  <input
                    type="text"
                    id="bce_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingClient?.bceNumber || ''}
                    onChange={(e) => setEditingClient({...editingClient, bceNumber: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={handleSaveEditedClient}
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Client Modal */}
      {showDeleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] modal-overlay"
          onClick={(e) => {
            // Close the modal if clicking outside the modal content
            if (e.target.classList.contains('modal-overlay')) {
              setShowDeleteModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Supprimer le client
            </h3>
            
            <p className="text-gray-700 mb-4">
              Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientTable; 